import React, { useRef, useState, useEffect } from 'react';
import { 
  Bold, Italic, Underline, Strikethrough, Quote, Code, 
  List, ListOrdered, Subscript, Superscript, IndentDecrease, 
  IndentIncrease, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Type, Palette, Image, RotateCcw, ChevronDown, Link, Unlink
} from 'lucide-react';

const RichTextEditor = ({ value, onChange,view_only=false, placeholder = "Enter text..." }) => {
  const editorRef = useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showFontSize, setShowFontSize] = useState(false);
  const [showFontFamily, setShowFontFamily] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [currentFormat, setCurrentFormat] = useState({});

  const colors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#FF0000', '#FF6600', '#FFCC00', '#FFFF00', '#99FF00', '#00FF00',
    '#00FFCC', '#00CCFF', '#0066FF', '#6600FF', '#CC00FF', '#FF0099'
  ];

  const fontSizes = [
    { label: 'Small', value: '12px' },
    { label: 'Normal', value: '14px' },
    { label: 'Large', value: '18px' },
    { label: 'Huge', value: '24px' }
  ];

  const fontFamilies = [
    'Arial, sans-serif',
    'Georgia, serif',
    'Times New Roman, serif',
    'Courier New, monospace',
    'Verdana, sans-serif',
    'Helvetica, sans-serif'
  ];

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    updateContent();
  };

  const updateContent = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    updateContent();
  };

  const handleKeyDown = (e) => {
    // Handle tab key for indentation
    if (e.key === 'Tab') {
      e.preventDefault();
      execCommand('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const createList = (ordered = false) => {
    execCommand(ordered ? 'insertOrderedList' : 'insertUnorderedList');
  };

  const setFontSize = (size) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (!range.collapsed) {
        const span = document.createElement('span');
        span.style.fontSize = size;
        try {
          range.surroundContents(span);
        } catch (e) {
          span.appendChild(range.extractContents());
          range.insertNode(span);
        }
        selection.removeAllRanges();
        updateContent();
      }
    }
    setShowFontSize(false);
  };

  const setFontFamily = (font) => {
    execCommand('fontName', font);
    setShowFontFamily(false);
  };

  const setTextColor = (color) => {
    execCommand('foreColor', color);
    setShowColorPicker(false);
  };

  const setBackgroundColor = (color) => {
    execCommand('backColor', color);
    setShowBgColorPicker(false);
  };

  const formatBlock = (tag) => {
    execCommand('formatBlock', tag);
  };

  const showLinkPrompt = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    setLinkText(selectedText);
    setLinkUrl('');
    setShowLinkDialog(true);
    
    // Store the current selection range
    if (selection.rangeCount > 0) {
      window.savedRange = selection.getRangeAt(0);
    }
  };

  const insertLink = () => {
    if (!linkUrl.trim()) return;

    const normalizedUrl = linkUrl.match(/^https?:\/\//i) ? linkUrl : `https://${linkUrl}`;
    
    // Restore the saved selection if it exists
    if (window.savedRange) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(window.savedRange);
    }
    
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    if (selectedText) {
      // If text is selected, create link with selected text
      const linkHtml = `<a href="${normalizedUrl}" target="_blank">${selectedText}</a>`;
      document.execCommand('insertHTML', false, linkHtml);
    } else if (linkText.trim()) {
      // If no text selected but link text provided, insert new link
      const linkHtml = `<a href="${normalizedUrl}" target="_blank">${linkText}</a>`;
      document.execCommand('insertHTML', false, linkHtml);
    } else {
      // If no text provided, use URL as text
      const linkHtml = `<a href="${normalizedUrl}" target="_blank">${normalizedUrl}</a>`;
      document.execCommand('insertHTML', false, linkHtml);
    }
    
    // Clear the saved range
    window.savedRange = null;
    
    setShowLinkDialog(false);
    setLinkUrl('');
    setLinkText('');
    updateContent();
    
    // Focus back to editor
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const removeLink = () => {
    execCommand('unlink');
  };

  const handleLinkKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      insertLink();
    }
  };

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <button
      type="button"
      className={`rte-toolbar-btn ${active ? 'rte-active' : ''}`}
      onClick={onClick}
      title={title}
      onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
    >
      {children}
    </button>
  );

  const DropdownButton = ({ isOpen, onToggle, children, title }) => (
    <div className="rte-dropdown-container">
      <button
        type="button"
        className={`rte-toolbar-btn rte-dropdown-btn ${isOpen ? 'rte-active' : ''}`}
        onClick={onToggle}
        title={title}
        onMouseDown={(e) => e.preventDefault()}
      >
        {children}
        <ChevronDown size={14} />
      </button>
    </div>
  );

  return (

    <div className="rte-rich-text-editor">
      {/* Toolbar */}
      {!view_only && (
      <div className="rte-toolbar">
        {/* Text Formatting */}
        
        <div className="rte-toolbar-group">
          <ToolbarButton onClick={() => execCommand('bold')} title="Bold">
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('italic')} title="Italic">
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('underline')} title="Underline">
            <Underline size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('strikeThrough')} title="Strikethrough">
            <Strikethrough size={16} />
          </ToolbarButton>
        </div>
       

        <div className="rte-toolbar-separator" />

        {/* Block Formatting */}
        <div className="rte-toolbar-group">
          <ToolbarButton onClick={() => execCommand('insertHTML', '<blockquote></blockquote>')} title="Blockquote">
            <Quote size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => formatBlock('pre')} title="Code Block">
            <Code size={16} />
          </ToolbarButton>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Headers */}
        <div className="rte-toolbar-group">
          <ToolbarButton onClick={() => formatBlock('h1')} title="Header 1">
            H1
          </ToolbarButton>
          <ToolbarButton onClick={() => formatBlock('h2')} title="Header 2">
            H2
          </ToolbarButton>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Lists */}
        <div className="rte-toolbar-group">
          <ToolbarButton onClick={() => createList(true)} title="Ordered List">
            <ListOrdered size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => createList(false)} title="Bullet List">
            <List size={16} />
          </ToolbarButton>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Links */}
        <div className="rte-toolbar-group">
          <ToolbarButton onClick={showLinkPrompt} title="Insert Link">
            <Link size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={removeLink} title="Remove Link">
            <Unlink size={16} />
          </ToolbarButton>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Script */}
        <div className="rte-toolbar-group">
          <ToolbarButton onClick={() => execCommand('subscript')} title="Subscript">
            <Subscript size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('superscript')} title="Superscript">
            <Superscript size={16} />
          </ToolbarButton>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Indent */}
        <div className="rte-toolbar-group">
          <ToolbarButton onClick={() => execCommand('outdent')} title="Decrease Indent">
            <IndentDecrease size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('indent')} title="Increase Indent">
            <IndentIncrease size={16} />
          </ToolbarButton>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Font Size */}
        <div className="rte-toolbar-group">
          <div className="rte-dropdown-container">
            <DropdownButton 
              isOpen={showFontSize} 
              onToggle={() => setShowFontSize(!showFontSize)}
              title="Font Size"
            >
              <Type size={16} />
            </DropdownButton>
            {showFontSize && (
              <div className="rte-dropdown-menu">
                {fontSizes.map((size) => (
                  <button
                    key={size.value}
                    className="rte-dropdown-item"
                    onClick={() => setFontSize(size.value)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Colors */}
        <div className="rte-toolbar-group">
          <div className="rte-dropdown-container">
            <DropdownButton 
              isOpen={showColorPicker} 
              onToggle={() => setShowColorPicker(!showColorPicker)}
              title="Text Color"
            >
              <Palette size={16} />
            </DropdownButton>
            {showColorPicker && (
              <div className="rte-dropdown-menu rte-color-picker">
                {colors.map((color) => (
                  <button
                    key={color}
                    className="rte-color-option"
                    style={{ backgroundColor: color }}
                    onClick={() => setTextColor(color)}
                    onMouseDown={(e) => e.preventDefault()}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="rte-dropdown-container">
            <DropdownButton 
              isOpen={showBgColorPicker} 
              onToggle={() => setShowBgColorPicker(!showBgColorPicker)}
              title="Background Color"
            >
              <div className="rte-bg-color-icon">A</div>
            </DropdownButton>
            {showBgColorPicker && (
              <div className="rte-dropdown-menu rte-color-picker">
                {colors.map((color) => (
                  <button
                    key={color}
                    className="rte-color-option"
                    style={{ backgroundColor: color }}
                    onClick={() => setBackgroundColor(color)}
                    onMouseDown={(e) => e.preventDefault()}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Font Family */}
        <div className="rte-toolbar-group">
          <div className="rte-dropdown-container">
            <DropdownButton 
              isOpen={showFontFamily} 
              onToggle={() => setShowFontFamily(!showFontFamily)}
              title="Font Family"
            >
              Font
            </DropdownButton>
            {showFontFamily && (
              <div className="rte-dropdown-menu rte-font-menu">
                {fontFamilies.map((font) => (
                  <button
                    key={font}
                    className="rte-dropdown-item"
                    style={{ fontFamily: font }}
                    onClick={() => setFontFamily(font)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {font.split(',')[0]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rte-toolbar-separator" />

        {/* Alignment */}
        <div className="rte-toolbar-group">
          <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Align Left">
            <AlignLeft size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Align Center">
            <AlignCenter size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('justifyRight')} title="Align Right">
            <AlignRight size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('justifyFull')} title="Justify">
            <AlignJustify size={16} />
          </ToolbarButton>
        </div>
        
        <div className="rte-toolbar-separator" />
      
      

      </div>
      )}
      
       

      {/* Link Dialog */}
      {(showLinkDialog && !view_only) && (
        <div className="rte-modal-overlay" onClick={() => {
          setShowLinkDialog(false);
          window.savedRange = null;
        }}>
          <div className="rte-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="rte-modal-title">Insert Link</h3>
            <div className="rte-modal-content">
              <div className="rte-input-group">
                <label className="rte-label">URL:</label>
                <input
                  type="url"
                  className="rte-input"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={handleLinkKeyPress}
                  placeholder="https://example.com"
                  autoFocus
                />
              </div>
              {!linkText && (
                <div className="rte-input-group">
                  <label className="rte-label">Link Text:</label>
                  <input
                    type="text"
                    className="rte-input"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    onKeyDown={handleLinkKeyPress}
                    placeholder="Link text (optional)"
                  />
                </div>
              )}
              {linkText && (
                <div className="rte-input-group">
                  <label className="rte-label">Selected Text:</label>
                  <div className="rte-selected-text">"{linkText}"</div>
                </div>
              )}
            </div>
            <div className="rte-modal-actions">
              <button 
                className="rte-btn rte-btn-secondary" 
                onClick={() => {
                  setShowLinkDialog(false);
                  window.savedRange = null;
                }}
              >
                Cancel
              </button>
              <button 
                className="rte-btn rte-btn-primary" 
                onClick={insertLink}
                disabled={!linkUrl.trim()}
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      <div
        ref={editorRef}
        className="rte-editor-content"
        contentEditable={!view_only}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      <style jsx>{`
        .rte-rich-text-editor {
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .rte-toolbar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          padding: 8px;
          border-bottom: 1px solid #eee;
          background: #fafafa;
          gap: 4px;
        }

        .rte-toolbar-group {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .rte-toolbar-separator {
          width: 1px;
          height: 24px;
          background: #ddd;
          margin: 0 4px;
        }

        .rte-toolbar-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          color: #555;
          transition: all 0.2s;
          font-size: 12px;
          font-weight: 600;
        }

        .rte-toolbar-btn:hover {
          background: #e9ecef;
          color: #333;
        }

        .rte-toolbar-btn.rte-active {
          background: #007bff;
          color: white;
        }

        .rte-dropdown-btn {
          width: auto;
          padding: 0 8px;
          gap: 4px;
        }

        .rte-dropdown-container {
          position: relative;
        }

        .rte-dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          z-index: 1000;
          min-width: 120px;
          max-height: 200px;
          overflow-y: auto;
        }

        .rte-dropdown-item {
          display: block;
          width: 100%;
          padding: 8px 12px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          transition: background 0.2s;
        }

        .rte-dropdown-item:hover {
          background: #f8f9fa;
        }

        .rte-color-picker {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 4px;
          padding: 8px;
          width: 180px;
        }

        .rte-color-option {
          width: 24px;
          height: 24px;
          border: 1px solid #ddd;
          border-radius: 2px;
          cursor: pointer;
          transition: transform 0.2s;
        }

        .rte-color-option:hover {
          transform: scale(1.1);
          border-color: #333;
        }

        .rte-bg-color-icon {
          font-weight: bold;
          font-size: 14px;
          background: linear-gradient(45deg, red 25%, blue 25%, blue 50%, red 50%, red 75%, blue 75%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .rte-font-menu {
          min-width: 160px;
        }

        .rte-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }

        .rte-modal {
          background: white;
          border-radius: 8px;
          padding: 20px;
          width: 400px;
          max-width: 90vw;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .rte-modal-title {
          margin: 0 0 16px 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }

        .rte-modal-content {
          margin-bottom: 20px;
        }

        .rte-input-group {
          margin-bottom: 16px;
        }

        .rte-label {
          display: block;
          margin-bottom: 4px;
          font-weight: 500;
          color: #333;
          font-size: 14px;
        }

        .rte-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }

        .rte-input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
        }

        .rte-modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .rte-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .rte-btn-secondary {
          background: #f8f9fa;
          color: #333;
          border: 1px solid #ddd;
        }

        .rte-btn-secondary:hover {
          background: #e9ecef;
        }

        .rte-btn-primary {
          background: #007bff;
          color: white;
        }

        .rte-btn-primary:hover:not(:disabled) {
          background: #0056b3;
        }

        .rte-btn-primary:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .rte-selected-text {
          padding: 8px 12px;
          background: #f8f9fa;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-style: italic;
          color: #666;
        }

        .rte-editor-content {
          min-height: 150px;
          padding: 12px;
          outline: none;
          line-height: 1.5;
          font-size: 14px;
        }

        .rte-editor-content:empty:before {
          content: attr(data-placeholder);
          color: #999;
          pointer-events: none;
        }

        .rte-editor-content blockquote {
          margin: 0;
          padding: 8px 16px;
          border-left: 4px solid #ddd;
          background: #f8f9fa;
          font-style: italic;
        }

        .rte-editor-content pre {
          background: #f8f9fa;
          padding: 12px;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          overflow-x: auto;
        }

        .rte-editor-content h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.5em 0;
        }

        .rte-editor-content h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.5em 0;
        }

        .rte-editor-content ul, .rte-editor-content ol {
          padding-left: 2em;
          margin: 0.5em 0;
        }

        .rte-editor-content img {
          max-width: 100%;
          height: auto;
        }

        .rte-editor-content a {
          color: #007bff;
          text-decoration: underline;
        }

        .rte-editor-content a:hover {
          color: #0056b3;
        }
      `}</style>
    </div>
  );
};


export default RichTextEditor;